import { useEffect, useRef, useState, type RefObject } from 'react';

import { useValidateAndSanitize } from '../number-spin-button/hooks/useValidateAndSanitize.js';
import { useValueNavigation } from '../number-spin-button/hooks/useValueNavigation.js';
import {
  NumberSpinButtonProps,
  NumberSpinButtonRef,
} from '../number-spin-button/types.js';

/**
 * This hook provides text selection and cursor utility functions.
 * Because the SpinButton is a contenteditable span, this is not provided natively.
 * @internal
 */
export const useNumberSpinButton = (
  props: Pick<
    NumberSpinButtonProps,
    | 'displayFormat'
    | 'required'
    | 'hasError'
    | 'minValue'
    | 'maxValue'
    | 'disabled'
    | 'readOnly'
    | 'value'
    | 'onChange'
  >,
): Pick<
  NumberSpinButtonProps,
  | 'displayFormat'
  | 'required'
  | 'hasError'
  | 'minValue'
  | 'maxValue'
  | 'disabled'
  | 'readOnly'
  | 'value'
  | 'onChange'
  | 'onKeyDownHandler'
> & {
  ref: RefObject<NumberSpinButtonRef | null> | null;
  onChange: (value: number | null) => void;
} => {
  const {
    disabled,
    readOnly,
    displayFormat,
    required,
    minValue,
    maxValue,
    hasError,
    value,
    onChange,
  } = props;

  const ref = useRef<NumberSpinButtonRef>(null);

  const [internalValue, setInternalValue] = useState<number | null | undefined>(
    value,
  );

  const [internalError, setInternalError] = useState<boolean | undefined>(
    hasError,
  );

  const { validate } = useValidateAndSanitize(displayFormat, required);

  const { onKeyDownHandler } = useValueNavigation(ref?.current?.element, {
    value: internalValue,
    disabled,
    readOnly,
    onChange: (value) => {
      if (value !== internalValue) {
        setInternalValue(value);
        onChange?.(value);
      }
    },
    displayFormat,
    required,
    minValue: minValue ?? Number.MIN_SAFE_INTEGER,
    maxValue: maxValue ?? Number.MAX_SAFE_INTEGER,
  });

  // Set error state on value change
  useEffect(() => {
    const isValid = validate(internalValue ?? null);
    setInternalError(!isValid);
  }, [setInternalError, validate, internalValue]);

  return {
    value: internalValue,
    hasError: internalError,
    onKeyDownHandler,
    required,
    readOnly,
    minValue,
    maxValue,
    disabled,
    displayFormat,
    ref,
    onChange: (value) => {
      onChange?.(value);
      value !== internalValue && setInternalValue(value);
    },
  };
};
